/**
 * Mollie OAuth API Service
 * Handles OAuth connection and data fetching from Mollie
 */

const API_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

class MollieService {
  /**
   * Get the Mollie OAuth authorize URL (redirects user to Mollie)
   */
  async getAuthorizeUrl(token) {
    const redirectUri = `${window.location.origin}/auth/mollie/callback`;
    const response = await fetch(
      `${API_URL}/api/connections/mollie/authorize?redirect_uri=${encodeURIComponent(redirectUri)}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) throw new Error("Failed to get Mollie authorize URL");
    return response.json();
  }

  /**
   * Exchange OAuth code for tokens (called from callback page)
   */
  async handleOAuthCallback(code, token) {
    const redirectUri = `${window.location.origin}/auth/mollie/callback`;
    const response = await fetch(`${API_URL}/api/connections/mollie/callback`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ code, redirect_uri: redirectUri }),
    });
    if (!response.ok) throw new Error("Failed to exchange Mollie OAuth code");
    return response.json();
  }

  /**
   * Save the company's own Mollie API key (permanent connection, no OAuth).
   */
  async saveApiKey(token, apiKey) {
    const response = await fetch(`${API_URL}/api/connections/mollie/apikey`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ apiKey }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.message || "Kon de Mollie API-key niet opslaan");
    }
    return data;
  }

  /**
   * Get Mollie organization & profiles info
   */
  async getInfo(token) {
    const response = await fetch(`${API_URL}/api/connections/mollie/info`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error("Failed to fetch Mollie info");
    }
    return response.json();
  }

  /**
   * Get Mollie payments
   */
  async getPayments(token, { limit = 50, from } = {}) {
    const params = new URLSearchParams({ limit: String(limit) });
    if (from) params.set("from", from);
    const response = await fetch(
      `${API_URL}/api/connections/mollie/payments?${params.toString()}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      },
    );
    if (!response.ok) {
      if (response.status === 404) return null;
      throw new Error("Failed to fetch Mollie payments");
    }
    return response.json();
  }

  /**
   * Check if Mollie is connected (by checking connections)
   */
  async checkConnection(token, companyId) {
    const response = await fetch(`${API_URL}/api/connections/${companyId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) return false;
    const data = await response.json();
    const mollieConn = (data.connections || []).find(
      (c) => c.platform === "mollie",
    );
    return mollieConn || null;
  }

  /**
   * Disconnect Mollie
   */
  async disconnect(token, companyId) {
    const response = await fetch(`${API_URL}/api/connections/mollie`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ companyId }),
    });
    return response.ok;
  }
}

export default new MollieService();
